import type { PlayerRoute } from '../../../app/appStore';
import type { MessageKey } from '../../../shared/i18n/messages';
import { marketCopyKey } from '../../../shared/country/marketCopy';

/**
 * The FAQ catalogue — 27 answers, every one of them illustrated.
 *
 * Each entry follows the same shape: the answer in one sentence, a picture,
 * a few numbered points, and somewhere to go next. That order is deliberate —
 * the accordion this replaced buried its conclusion under the explanation.
 *
 * `aliases` is the load-bearing field for search. It holds what users type,
 * which is rarely what we call it: nobody searches "rollover" or "VOID", they
 * search "dinheiro travado" and "sumiu minha aposta".
 */

export type FaqCategory = 'start' | 'money' | 'fair' | 'games' | 'raffle' | 'rewards' | 'account';

export type FaqIllustrationId =
  | 'start'
  | 'app'
  | 'deposit'
  | 'withdraw'
  | 'currencies'
  | 'prizemissing'
  | 'rigged'
  | 'calc'
  | 'verify'
  | 'chainstall'
  | 'lock'
  | 'howtobet'
  | 'betlimits'
  | 'rejected'
  | 'history'
  | 'lastdigit'
  | 'sicbo'
  | 'roulette'
  | 'rafflehow'
  | 'raffletickets'
  | 'raffleprize'
  | 'rafflecancel'
  | 'vip'
  | 'missions'
  | 'password'
  | 'limits'
  | 'legal';

export type FaqEntry = {
  id: FaqIllustrationId;
  category: FaqCategory;
  /** i18n keys are derived: faq_<id>_q / _tldr / _points / _fig / _keys / _cta. */
  action?: PlayerRoute;
};

export const FAQ_CATEGORIES: Array<{ id: FaqCategory; label: MessageKey; emoji: string }> = [
  { id: 'start', label: 'faqCat_start', emoji: '👋' },
  { id: 'money', label: 'faqCat_money', emoji: '💰' },
  { id: 'fair', label: 'faqCat_fair', emoji: '🛡️' },
  { id: 'games', label: 'faqCat_games', emoji: '🎲' },
  { id: 'raffle', label: 'faqCat_raffle', emoji: '🎟️' },
  { id: 'rewards', label: 'faqCat_rewards', emoji: '🎁' },
  { id: 'account', label: 'faqCat_account', emoji: '👤' },
];

export const FAQ_ENTRIES: FaqEntry[] = [
  { id: 'start', category: 'start', action: 'lobby' },
  { id: 'app', category: 'start' },

  { id: 'deposit', category: 'money', action: 'wallet' },
  { id: 'withdraw', category: 'money', action: 'wallet' },
  { id: 'currencies', category: 'money' },
  { id: 'prizemissing', category: 'money', action: 'bet-history' },

  { id: 'rigged', category: 'fair', action: 'rules' },
  { id: 'calc', category: 'fair', action: 'rules' },
  { id: 'verify', category: 'fair', action: 'archive' },
  { id: 'chainstall', category: 'fair' },

  { id: 'lock', category: 'games' },
  { id: 'howtobet', category: 'games' },
  { id: 'betlimits', category: 'games', action: 'lobby' },
  { id: 'rejected', category: 'games', action: 'avisos.help' },
  { id: 'history', category: 'games', action: 'bet-history' },
  { id: 'lastdigit', category: 'games', action: 'rules' },
  { id: 'sicbo', category: 'games', action: 'rules' },
  { id: 'roulette', category: 'games', action: 'rules' },

  { id: 'rafflehow', category: 'raffle', action: 'sorteios' },
  { id: 'raffletickets', category: 'raffle', action: 'me.activities' },
  { id: 'raffleprize', category: 'raffle', action: 'rules' },
  { id: 'rafflecancel', category: 'raffle' },

  { id: 'vip', category: 'rewards', action: 'me.vip' },
  { id: 'missions', category: 'rewards', action: 'rewards' },

  { id: 'password', category: 'account', action: 'me.security' },
  { id: 'limits', category: 'account', action: 'me.limits' },
  { id: 'legal', category: 'account' },
];

/** All six per-entry message keys follow from the id, so the catalogue above
 *  stays a list of ids and never drifts out of step with the locale tables.
 *
 *  The money answers (deposit, withdraw, currencies) and the two figures that
 *  say "deposit via Pix" describe Brazil's rail; in any other market
 *  `marketCopyKey` swaps in the neutral twin. Keys without a twin pass through
 *  unchanged, so the catalogue never has to know which entries are affected. */
export function faqKeys(entry: FaqEntry, countryCode: string | null | undefined) {
  const key = (part: string) => marketCopyKey(`faq_${entry.id}_${part}` as MessageKey, countryCode);
  return {
    question: key('q'),
    tldr: key('tldr'),
    points: key('points'),
    figure: key('fig'),
    aliases: key('keys'),
    cta: key('cta'),
  };
}
